/**
 * MAHI discovery feed — one full-bleed product card + the swipe gesture.
 *
 * Same controlled-exit-animation pattern as StyleSwipeDeck (a `useMotionValue`
 * drives live drag feedback; the actual exit is a separate, state-driven
 * `animate` target rather than relying on framer-motion's function-form
 * `exit` prop, which doesn't reliably fire the way its docs imply).
 *
 * Direction on release, in priority order:
 *   - |y| past the up-threshold and bigger than |x|  → try it on, in place
 *     (see useTryOnJob) — the card never actually leaves, so there is no
 *     'up' exit animation to speak of, just the same snapback as a drag
 *     that crossed no threshold at all.
 *   - |x| past the side-threshold                     → 'left' | 'right',
 *     the only two directions `exitDirection` (from the parent) is ever set
 *     to, and the only two that actually retire this card from the deck.
 *   - otherwise                                        → springs back to
 *     center (framer-motion's native drag-constraint snapback, no code
 *     needed for that case).
 *
 * A tap (no meaningful drag) calls `onTap` instead — framer-motion's `onTap`
 * gesture already distinguishes this from a drag correctly.
 */
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useMotionValue, useTransform } from 'framer-motion';
import { Maximize2, RotateCcw, Share2 } from 'lucide-react';
import toast from 'react-hot-toast';
import mahi, { buyLink, recordBuyIntent } from '../../api';
import { effectivePrice, formatMoney } from '../../currency';
import useTryOnJob from './useTryOnJob';
import TryOnProgress from './TryOnProgress';
import TryOnPhotoSheet from './TryOnPhotoSheet';
import { useMahiAuth } from '../../auth';

const SIDE_THRESHOLD_FRACTION = 1 / 3;
const UP_THRESHOLD_FRACTION = 1 / 4; // full card height rarely fits on screen; a quarter reads as deliberate
const PHOTO_CROSSFADE_MS = 3800;
// How far ahead of a crossfade the next frame is mounted. Long enough that it
// has downloaded and decoded before its opacity animates in, short enough that
// it isn't competing with the hero image for bandwidth during first paint.
const PHOTO_PRELOAD_LEAD_MS = 2200;

// Tapping a card opens the full-screen photo viewer, but nothing on the card
// said so — tourSteps.js assumed that was "discoverable by looking at it" and
// real users were not finding it. Rather than a sixth tour step (which delays
// the first dress, the exact cost that file warns about), the affordance now
// lives on the card permanently: a small expand chip, always visible.
//
// The chip is `pointer-events-none` on purpose. The whole card is already the
// tap target — making the chip clickable would create a second, smaller target
// that competes with the drag gesture for no benefit.
//
// Shown once as an explicit worded hint ("Tap to view full screen"), then
// reduced to the icon alone forever after. localStorage, not the profile row:
// this is a per-device UI nudge, not a fact about the person, and a failed
// write should cost nothing.
const FULLSCREEN_HINT_KEY = 'mahi_fullscreen_hint_seen';
const FULLSCREEN_HINT_MS = 5200;

function hasSeenFullscreenHint() {
  try {
    return localStorage.getItem(FULLSCREEN_HINT_KEY) === '1';
  } catch {
    return true; // private mode / storage blocked — don't nag on every card
  }
}

function markFullscreenHintSeen() {
  try {
    localStorage.setItem(FULLSCREEN_HINT_KEY, '1');
  } catch { /* storage unavailable — the icon alone still teaches it */ }
}

const EXIT_TARGETS = {
  right: { x: 600, y: 0, opacity: 0, rotate: 22 },
  left:  { x: -600, y: 0, opacity: 0, rotate: -22 },
};

// A right-swipe can instead be aimed at a point on screen — the wardrobe
// button — so the card visibly goes somewhere rather than just off the edge.
// The card shrinks as it travels, which is what makes it read as "into that",
// not "past that". Falls back to the plain off-screen exit whenever no target
// is supplied, so the gesture still works before layout has settled.
function flyToTarget(offset) {
  return { x: offset.x, y: offset.y, scale: 0.08, opacity: 0, rotate: 8 };
}

function PriceRow({ product }) {
  const { current, was, isDiscounted } = effectivePrice(product);
  if (current == null) return null;
  const cur = product.currency;
  return (
    <div className="flex items-center gap-2">
      {isDiscounted && (
        <span className="text-white/60 line-through text-sm">{formatMoney(was, cur)}</span>
      )}
      <span className="text-white font-semibold text-lg">{formatMoney(current, cur)}</span>
    </div>
  );
}

// JS port of backend/services/mahi/taxonomy.py's label() — same irregulars,
// same title-case fallback — so a chip reads "A-line", not "A line". Kept
// tiny and local rather than shared: this is the only place on the frontend
// that displays an attrs.* value today.
const ATTR_LABEL_OVERRIDES = {
  tshirt: 'T-shirt',
  two_piece: '2-piece set',
  three_piece_plus: '3-piece set or more',
  single: 'Single piece',
  co_ord_set: 'Co-ord set',
  dress_material: 'Dress material (unstitched)',
  v_neck: 'V-neck',
  a_line: 'A-line',
  three_quarter: 'Three-quarter',
  off_shoulder: 'Off-shoulder',
  multicolour: 'Multicolour',
  block_print: 'Block print',
  tie_dye: 'Tie-dye',
  ikat: 'Ikat',
  bandhani: 'Bandhani',
  khadi: 'Khadi',
  chanderi: 'Chanderi',
};

function attrLabel(value) {
  if (!value || value === 'unknown') return '';
  if (ATTR_LABEL_OVERRIDES[value]) return ATTR_LABEL_OVERRIDES[value];
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Vibe chips: fit, occasion and category, in that order, skipping whatever a
// given product's enrichment hasn't resolved yet. Reads only `attrs.*` — the
// enum-validated taxonomy fields — never the raw crawled `category`/`color`/
// `fabric` strings, which are free text off 26 different storefronts and not
// safe to show as if they were a clean tag (see taxonomy.py's own docstring).
// Most cards won't have `attrs` populated yet, which is a real enrichment-
// coverage gap, not something to paper over here — so this renders nothing
// rather than guessing.
function VibeChips({ product }) {
  const attrs = product.attrs;
  if (!attrs) return null;
  const chips = ['fit', 'occasion', 'category']
    .map((key) => attrLabel(attrs[key]))
    .filter(Boolean)
    .slice(0, 3);
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((chip) => (
        <span
          key={chip}
          className="text-[10.5px] font-semibold text-white bg-white/14 backdrop-blur-sm border border-white/26 rounded-full px-2.5 py-1"
        >
          {chip}
        </span>
      ))}
    </div>
  );
}

export default function ProductSwipeCard({
  product, isTop, stackIndex, onSwipe, onTap, onTryOn, onTryOnUpdate, exitDirection, saveTargetOffset,
}) {
  // Their own photo, for the converging-cards animation while a try-on
  // renders — see TryOnProgress.
  const { user } = useMahiAuth();
  const userPhoto = user?.picture;

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-300, 300], [-18, 18]);

  // Try-on lives on the card itself, not a full-screen takeover: the result
  // replaces this card's own photo in place once it lands. `start` is what
  // both the button and the up-drag gesture call; `status`/`job` drive what
  // the card shows in the meantime.
  const { start: startTryOn, status: tryOnStatus, job: tryOnJob, error: tryOnError } = useTryOnJob(product.mira_sku);
  const reactionRequestedRef = useRef(false);

  // Owned here rather than by TryOnProgress: that overlay only renders while
  // `rendering` is true, and a native OS photo picker routinely outlasts an
  // 18-30s try-on. If the sheet's own mount lived inside TryOnProgress, the
  // job finishing mid-pick would unmount it — and the hidden file input with
  // it — right as the user's selection came back, silently dropping it. The
  // card itself persists for the try-on's whole lifecycle (see the comment
  // above `rendering`), so anchoring the sheet here instead means it survives
  // the job settling either way.
  const [photoSheetOpen, setPhotoSheetOpen] = useState(false);

  // The card is busy. One flag, checked everywhere, so the frame cannot end up
  // half handed over — which is what happened when the progress panel and the
  // product chrome were decided independently and both drew over the bottom of
  // the card. Only the top card can be rendering; a background card has no
  // try-on of its own to show.
  const rendering = isTop && tryOnStatus === 'running';

  const triggerTryOn = () => {
    if (tryOnStatus === 'running' || tryOnStatus === 'ready') return;
    onTryOn?.();
    startTryOn();
  };

  // Share links come from the server (POST /tryon/jobs/:id/share) — the
  // photo lives on our own S3, not on the device, so there's no local file
  // to hand a share sheet directly. navigator.share opens the OS-level
  // sheet (WhatsApp/Instagram/Messages all show up there on a phone) when
  // it's available; a desktop browser without it just gets the link copied,
  // which is exactly as shareable. Called fresh on every tap rather than
  // cached client-side, but that's cheap and safe to do: the endpoint itself
  // (tryon_shares.get_or_create) hands back the same still-live link instead
  // of writing a new row every time, so repeated taps don't pile up shares.
  const [sharing, setSharing] = useState(false);

  const handleShare = async (e) => {
    e.stopPropagation();
    if (sharing || !tryOnJob?.job_id) return;
    setSharing(true);
    try {
      const { data } = await mahi.post(`/tryon/jobs/${tryOnJob.job_id}/share`);
      // No `text` field here on purpose: some share targets glue `text` and
      // `url` together into one string when the recipient forwards/copies
      // it, so the pasted "link" becomes the URL plus the product name
      // stuck to the end of it — which 404s since it's not a valid path.
      const shareData = {
        title: 'See what I tried on with MAHI',
        url: data.url,
      };
      if (navigator.share && navigator.canShare?.(shareData) !== false) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(data.url);
        toast.success('Link copied - share it anywhere');
      }
    } catch (err) {
      // AbortError just means they closed the native share sheet — not a failure.
      if (err?.name !== 'AbortError') toast.error("Couldn't create that link - try again.");
    } finally {
      setSharing(false);
    }
  };

  useEffect(() => {
    if (tryOnStatus === 'failed') {
      toast.error(tryOnError || "That try-on didn't work - give it another go.");
    }
  }, [tryOnStatus, tryOnError]);

  // One optional line in MAHI's own voice about the garment, fetched (and
  // persisted server-side onto the job) once the try-on image lands — never
  // about how the photo looks, per the same rule as everywhere else she's
  // near a body. Fire-and-persist only: this screen doesn't display it
  // (see Style Studio's detail view, which does, from the same persisted
  // field) — showing it here too was one more thing crowding a phone screen
  // that's already tight on room right after a try-on lands.
  useEffect(() => {
    if (tryOnStatus !== 'ready' || !tryOnJob?.job_id || reactionRequestedRef.current) return;
    reactionRequestedRef.current = true;
    mahi.post('/agent/tryon-reaction', { mira_sku: product.mira_sku, tryon_id: tryOnJob.job_id }).catch(() => {});
  }, [tryOnStatus, tryOnJob, product.mira_sku]);

  // The result image renders outside this card entirely, in the full-screen
  // photo viewer — reported up rather than drawn here. Only the top card's
  // word counts; a background card's own try-on state, if it somehow has
  // one, shouldn't fight it for the same viewer.
  useEffect(() => {
    if (!isTop) return;
    onTryOnUpdate?.({ imageUrl: tryOnStatus === 'ready' ? tryOnJob?.image_url || null : null });
  }, [isTop, tryOnStatus, tryOnJob, onTryOnUpdate]);

  const saveGlow = useTransform(x, [40, 160], [0, 1]);
  const passGlow = useTransform(x, [-160, -40], [1, 0]);
  const tryonGlow = useTransform(y, [-160, -40], [1, 0]);

  // The expand chip sits where the swipe verdict chips ("To the wardrobe" /
  // "Not this one") appear, so it has to be gone by the time they arrive.
  // Fading it out on any meaningful drag also keeps the card clean during the
  // gesture, when nobody is reading it anyway.
  const hintOpacity = useTransform([x, y], ([lx, ly]) => {
    const drift = Math.max(Math.abs(lx), Math.abs(ly));
    if (drift <= 16) return 1;
    return Math.max(0, 1 - (drift - 16) / 44);
  });

  // Worded hint on the first card only; the icon alone carries it afterwards.
  const [showWordedHint, setShowWordedHint] = useState(() => !hasSeenFullscreenHint());
  useEffect(() => {
    if (!isTop || !showWordedHint) return undefined;
    const timer = setTimeout(() => {
      setShowWordedHint(false);
      markFullscreenHintSeen();
    }, FULLSCREEN_HINT_MS);
    return () => clearTimeout(timer);
  }, [isTop, showWordedHint]);

  // framer-motion's `onTap` can still fire after a genuine drag (any
  // direction) or after a press that started on a child button — its tap
  // and drag/click gesture recognizers don't reliably coordinate with each
  // other or with propagation stopped further down the tree. Rather than
  // fight that via event-propagation timing, guard at the one place fully
  // under our control: the `onTap` callback itself. `onDragStart` only ever
  // fires for a real drag (past framer's own activation threshold), so it's
  // a reliable "a swipe attempt just happened, don't also open detail" flag
  // — including an aborted drag that snaps back without swiping.
  const suppressTapRef = useRef(false);
  const suppressNextTap = () => {
    suppressTapRef.current = true;
    // Safety net: nothing guarantees `onTap` fires to consume this flag (a
    // button press might resolve without a tap at all) — don't let a stale
    // flag silently eat the next genuine tap on the photo.
    setTimeout(() => { suppressTapRef.current = false; }, 400);
  };
  const handleDragStart = () => { suppressTapRef.current = true; };
  const handleCardTap = (e, info) => {
    if (suppressTapRef.current) {
      suppressTapRef.current = false;
      return;
    }
    // They just used it — the worded hint has done its job and shouldn't
    // reappear on the next card.
    if (showWordedHint) {
      setShowWordedHint(false);
      markFullscreenHintSeen();
    }
    onTap?.(e, info);
  };

  const restTarget = { scale: 1 - stackIndex * 0.035, y: stackIndex * 14, x: 0, rotate: 0, opacity: stackIndex > 2 ? 0 : 1 };
  // A right-swipe flies into the wardrobe button when we know where it is;
  // otherwise it exits off the right edge exactly as before.
  const exitTarget =
    exitDirection === 'right' && saveTargetOffset
      ? flyToTarget(saveTargetOffset)
      : EXIT_TARGETS[exitDirection];
  const animateTarget = isTop && exitDirection ? exitTarget : restTarget;

  const handleDragEnd = (e, info) => {
    if (typeof window === 'undefined') return;
    const sideThreshold = window.innerWidth * SIDE_THRESHOLD_FRACTION;
    const upThreshold = window.innerHeight * UP_THRESHOLD_FRACTION;
    const { x: ox, y: oy } = info.offset;

    if (-oy > upThreshold && -oy > Math.abs(ox)) {
      triggerTryOn();
    } else if (Math.abs(ox) > sideThreshold) {
      onSwipe(ox > 0 ? 'right' : 'left');
    }
    // else: no onSwipe call — framer-motion's own drag constraints spring
    // the card back to (0,0) automatically.
  };

  const photos = product.image_urls?.length ? product.image_urls : [];
  const title = product.display_title || product.title;

  // Ambient slideshow on the card itself, not just the full-screen viewer —
  // only the top card runs it (background cards are barely visible anyway).
  // Resets automatically on every product since ProductSwipeCard is keyed
  // by mira_sku in DiscoveryFeed, so each product gets a fresh mount.
  const [photoIndex, setPhotoIndex] = useState(0);

  // How many frames of the roll are actually in the DOM. Previously every
  // entry of `image_urls` was mounted immediately on every one of the three
  // stacked cards — ~18 full-size images streaming over one HTTP/2 connection,
  // so the one image the user is waiting on got a fraction of the pipe and the
  // card sat on its grey placeholder for seconds on mobile data. Now the roll
  // is revealed one frame at a time, just ahead of the crossfade that needs it.
  //
  // High-water mark rather than a window: it only ever grows, so wrapping back
  // to frame 0 doesn't unmount the later frames and re-download them on the
  // next lap.
  const [mountedCount, setMountedCount] = useState(1);

  useEffect(() => {
    if (!isTop || photos.length < 2) return undefined;
    const next = (photoIndex + 1) % photos.length;
    const preload = setTimeout(
      () => setMountedCount((c) => Math.max(c, next + 1)), PHOTO_PRELOAD_LEAD_MS);
    const timer = setTimeout(() => setPhotoIndex(next), PHOTO_CROSSFADE_MS);
    return () => { clearTimeout(preload); clearTimeout(timer); };
  }, [isTop, photoIndex, photos.length]);

  // A background card is 96%-scaled and almost entirely hidden behind the one
  // in front of it, so its roll is never seen — but its first frame does need
  // to be ready for the moment it becomes the top card, which is why it loads
  // at all rather than waiting.
  const mountedPhotos = photos.slice(0, isTop ? mountedCount : 1);

  return (
    <motion.div
      className="absolute inset-0"
      style={{ zIndex: 10 - stackIndex, x: isTop && !exitDirection ? x : undefined, y: isTop && !exitDirection ? y : undefined, rotate: isTop && !exitDirection ? rotate : undefined }}
      animate={animateTarget}
      transition={
        exitDirection === 'right' && saveTargetOffset
          ? { duration: 0.46, ease: [0.32, 0, 0.28, 1] }
          : { duration: exitDirection ? 0.32 : 0.25 }
      }
      // Locked while a render is in flight. There is nothing to do with this
      // card until the result lands, and swiping it away would tear down the
      // very job it is waiting for — useTryOnJob lives here and cancels on
      // unmount.
      drag={isTop && !exitDirection && !rendering}
      // Locked to whichever axis the gesture actually started on. Without
      // this, both axes stay live for the whole drag: an upward "try it on"
      // pull also slid the card sideways (and, via `rotate`, tilted it) for
      // every bit of horizontal wobble in the finger's path — which on a
      // phone is a lot. It also stops a sideways swipe drifting vertically
      // into the up-threshold. Purely a gesture-resolution change, identical
      // on every platform.
      dragDirectionLock
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      // 0.9 tracked the finger almost 1:1 well past the constraints, so the
      // card travelled nearly the full width of the screen on a gesture that
      // was never going to cross a threshold. Half that still reads as
      // responsive but keeps the card visibly anchored to its slot.
      dragElastic={0.5}
      // No coasting after release: momentum let the card keep moving after
      // the finger was gone, which is what made it look like the card (and
      // the page under it) never settled.
      dragMomentum={false}
      onDragStart={isTop && !rendering ? handleDragStart : undefined}
      onDragEnd={isTop && !rendering ? handleDragEnd : undefined}
      onTap={isTop && !rendering ? handleCardTap : undefined}
      data-testid={isTop ? 'discovery-top-card' : undefined}
    >
      {/* Rounding lives here rather than on the stage: the stage can no
          longer clip its children, because a right-swipe now flies the card
          out of the stage toward the wardrobe button and a clipping parent
          would cut it off mid-flight. */}
      <div className="group w-full h-full relative overflow-hidden rounded-[1.75rem] bg-slate-200 select-none cursor-zoom-in">
        {tryOnStatus === 'ready' && tryOnJob?.image_url ? (
          // The result replaces the product photo in place — same frame,
          // same buttons underneath — rather than taking over the screen.
          <motion.img
            key="tryon-result"
            src={tryOnJob.image_url}
            alt=""
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: 'easeInOut' }}
          />
        ) : (
          mountedPhotos.map((src, i) => {
            // The top card's opening frame is the one thing the user is
            // actually waiting on — it gets the connection ahead of the two
            // cards stacked behind it and ahead of the rest of its own roll.
            // Lowercase attribute names on purpose: React 18.2 has no built-in
            // fetchPriority prop and would drop the camelCase spelling, but it
            // passes unrecognised all-lowercase attributes straight to the DOM.
            const isHero = isTop && i === 0;
            return (
              <motion.img
                key={src + i}
                src={src}
                alt={title}
                draggable={false}
                fetchpriority={isHero ? 'high' : 'low'}
                decoding={isHero ? 'sync' : 'async'}
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                initial={false}
                animate={{ opacity: i === photoIndex ? 1 : 0 }}
                transition={{ duration: 1.6, ease: 'easeInOut' }}
              />
            );
          })
        )}

        {/* bottom scrim for text legibility over any photo. Gone while a
            try-on renders — there is no text under it then, and the gradient
            muddies the bottom of the progress panel for nothing. */}
        {!rendering && (
          <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/70 via-black/25 to-transparent pointer-events-none" />
        )}

        {/* The render takes 18-30 seconds, and this was a pill reading
            "Trying it on…" for all of it — long enough that the honest
            question becomes whether anything is happening at all. See
            TryOnProgress: it shows the two photos being combined, paces a
            bar, and gives them something worth reading meanwhile.
            It gets the whole card. Everything else on this frame — the
            brand, the title, the price, the buttons, the scrim, the expand
            chip — is hidden for the duration, because there is nothing to do
            with any of it while a render is in flight and the panel needs the
            room. All of it returns the moment the job settles, whether that
            is a result or a failure. */}
        {rendering && (
          <TryOnProgress
            userPhoto={userPhoto}
            garmentPhoto={photos[0]}
            garmentTitle={title}
            onChangePhoto={() => setPhotoSheetOpen(true)}
          />
        )}

        {/* Independent of `rendering` on purpose — see the state comment
            above. A pick that outlasts the job must still land. */}
        <AnimatePresence>
          {photoSheetOpen && <TryOnPhotoSheet onClose={() => setPhotoSheetOpen(false)} />}
        </AnimatePresence>

        {/* Full-screen affordance. Hidden while a try-on is rendering — the
            progress panel owns the card at that moment — and while the finger
            is dragging, so it never collides with the swipe verdict chips
            that occupy this same corner. */}
        {isTop && !rendering && (
          <motion.div
            style={{ opacity: hintOpacity }}
            className="absolute top-6 right-5 flex items-center gap-2 pointer-events-none"
            data-testid="fullscreen-affordance"
          >
            {showWordedHint && (
              <motion.span
                initial={{ opacity: 0, x: 6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, delay: 0.8 }}
                className="bg-black/55 backdrop-blur-sm text-white/90 text-xs font-mahi-sans rounded-full px-3 py-1.5 whitespace-nowrap"
              >
                Tap to view full screen
              </motion.span>
            )}
            <motion.span
              className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm ring-1 ring-white/25 flex items-center justify-center text-white/90 md:group-hover:bg-black/60 md:group-hover:scale-110 transition-all"
              // One slow pulse while the worded hint is up, then it settles.
              // Deliberately not a loop: a permanently animating chip on every
              // card would read as an error state, not an invitation.
              animate={showWordedHint ? { scale: [1, 1.12, 1] } : { scale: 1 }}
              transition={showWordedHint
                ? { duration: 1.6, repeat: 2, ease: 'easeInOut', delay: 0.8 }
                : { duration: 0.2 }}
            >
              <Maximize2 className="w-4 h-4" />
            </motion.span>
          </motion.div>
        )}

        {/* Swipe feedback. Gone while rendering, since dragging is disabled
            then — these are driven by the drag motion values and would just
            be dead layers over the progress panel. */}
        {isTop && !rendering && (
          <>
            {/* Soft corner wash instead of a full-card colored ring — the
                ring read as the Tinder like/nope border flash. */}
            <motion.div style={{ opacity: saveGlow }}
              className="absolute inset-0 bg-gradient-to-l from-raspberry-400/30 via-transparent to-transparent pointer-events-none" />
            <motion.div style={{ opacity: passGlow }}
              className="absolute inset-0 bg-gradient-to-r from-slate-500/20 via-transparent to-transparent pointer-events-none" />
            <motion.div style={{ opacity: tryonGlow }}
              className="absolute inset-0 bg-gradient-to-t from-mahiteal-400/30 via-transparent to-transparent pointer-events-none" />

            {/* Serif, ribbon-style tags in MAHI's own voice rather than
                bold rubber-stamp SAVE/PASS lettering. */}
            <motion.div style={{ opacity: saveGlow }}
              className="absolute top-7 right-6 rotate-[6deg] bg-white/95 backdrop-blur-sm border border-raspberry-200 text-raspberry-600 rounded-full px-4 py-1.5 font-mahi-serif italic text-base shadow-sm pointer-events-none">
              To the wardrobe
            </motion.div>
            <motion.div style={{ opacity: passGlow }}
              className="absolute top-7 left-6 rotate-[-6deg] bg-white/95 backdrop-blur-sm border border-slate-200 text-slate-500 rounded-full px-4 py-1.5 font-mahi-serif italic text-base shadow-sm pointer-events-none">
              Not this one
            </motion.div>
            <motion.div style={{ opacity: tryonGlow }}
              className="absolute top-7 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-sm border border-mahiteal-200 text-mahiteal-700 rounded-full px-4 py-1.5 font-mahi-serif italic text-base shadow-sm pointer-events-none">
              See it on me
            </motion.div>
          </>
        )}

        {/* Bottom layer: brand/title/price, then Try It On + Buy sit right
            beneath at the card's bottom edge — the two things you'd
            actually do with this dress, not tucked mid-photo. Both stop
            propagation in the CAPTURE phase (onPointerDownCapture), not
            the bubble phase — framer-motion attaches its own tap/drag
            gesture as a real native listener directly on this card's DOM
            node, which fires during native bubbling *before* React gets
            around to dispatching these buttons' own (delegated, root-
            level) onClick/onPointerDown handlers, so a capture-phase stop
            is needed to reliably beat it for the pointerdown that would
            otherwise start a press. That alone wasn't enough, though: a
            completed click still opens detail underneath the button, and a
            genuine swipe (any direction) fires `onTap` too — framer's tap
            gesture doesn't reliably get cancelled by a sibling drag or by
            propagation stopped further down the tree. `suppressNextTap()`
            (button press) and `onDragStart` (real drag) both set
            `suppressTapRef`, which `handleCardTap` checks before ever
            calling the parent's `onTap` — a guard on our own callback,
            immune to whatever framer's gesture internals actually do. */}
        {/* Hidden entirely while a render is in flight.
            This block was briefly given z-20 so brand, price and Buy would
            stay readable above the progress panel — "the render is
            interesting to watch, not a reason to stop selling". That was
            wrong in practice: at 140-odd pixels tall it sat exactly on top of
            the progress bar and the note, so the two most useful parts of the
            wait were the two you could not see. There is nothing to do with a
            buy button during a render anyway, and it all comes back the
            moment the job settles either way. */}
        {!rendering && (
          <div className="absolute inset-x-0 bottom-0 p-5 pb-6 flex flex-col gap-3 pointer-events-none">
            <div>
              {/* Hairline pill around the brand name. Same 10px / .24em /
                  uppercase / 70% white it always had — a short brand like
                  "ONLY" read as stray UI copy without a container to say
                  "this is a name". */}
              <span className="inline-block text-[10px] tracking-[0.24em] uppercase text-white/85 font-semibold font-mahi-sans border border-white/40 rounded-full px-2.5 py-1 mb-2">
                {product.brand}
              </span>
              <h3 className="font-mahi-serif text-2xl text-white leading-tight mb-2">{title}</h3>
              <PriceRow product={product} />
            </div>

            {/* Taxonomy answers "what is this?" — a question she's already
                stopped asking once a try-on result is on screen, so the
                chips step aside the same moment Share steps in. */}
            {tryOnStatus !== 'ready' && <VibeChips product={product} />}

            {isTop && (
              // Flex, not a centered grid: the old 3-column grid forced Buy Now
              // into a fraction of the row that shrank whenever the middle
              // button's own text was long ("Trying it on…"), wrapping "Buy
              // Now" onto two lines and leaving it visibly taller than its
              // neighbor. Share + the try-on button are grouped and sized to
              // their own content (never squeezed), and `ml-auto` pins Buy Now
              // to the end — nothing here can ever force either label to wrap.
              <div className="flex items-center gap-2 w-full">
                <div className={tryOnStatus === 'ready' ? 'flex-1 min-w-0' : 'flex items-center gap-2 min-w-0'}>
                  {/* Promoted from a 38px glass circle to the row's primary
                      button once a result is on screen — this is the app's
                      whole growth loop, and it was the quietest control on
                      the card. It takes the space "See it on me" leaves
                      behind (that button unmounts on `ready`, nothing else
                      grew to fill the gap) and carries the brand's own
                      coral-to-lavender wash, which neither MAHI's teal nor
                      commerce's raspberry claim — this moment is the
                      shopper's, not MAHI's or the store's. */}
                  {tryOnStatus === 'ready' && (
                    <button
                      type="button"
                      onClick={handleShare}
                      onPointerDownCapture={(e) => { e.stopPropagation(); suppressNextTap(); }}
                      disabled={sharing}
                      data-testid="card-share-button"
                      style={{ background: 'linear-gradient(118deg, #EE9B96 0%, #F2C4B1 44%, #C7A8D5 78%, #C693C9 100%)' }}
                      className="pointer-events-auto w-full whitespace-nowrap rounded-full text-[#33232E] text-sm font-semibold font-mahi-sans px-5 py-2.5 shadow-[0_12px_30px_-8px_rgba(198,147,201,0.95)] ring-1 ring-inset ring-white/35 active:scale-95 transition-transform inline-flex items-center justify-center gap-1.5"
                    >
                      <Share2 className="w-4 h-4" />
                      Share this look
                    </button>
                  )}
                  {/* Once the result is on screen, the picture is the feedback —
                      a disabled "That's me" chip restated it without offering
                      anything to do, and took row space from Share and Buy Now,
                      which both still have a job. Hidden on `ready` rather than
                      disabled: an unclickable control invites a click. */}
                  {/* No `running` state on this button any more: the whole
                      row is unmounted while a render is in flight, so a
                      "Trying it on…" label here could never be seen. The
                      progress panel says that now, at the size the message
                      deserves. Mahiteal rather than amber, matching the
                      wardrobe's own try-on button and the rule the palette
                      follows — raspberry is commerce, mahiteal is MAHI's own
                      doing. */}
                  {tryOnStatus !== 'ready' && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); triggerTryOn(); }}
                      onPointerDownCapture={(e) => { e.stopPropagation(); suppressNextTap(); }}
                      data-testid="card-tryon-button"
                      data-tour={isTop ? 'tryon' : undefined}
                      className="pointer-events-auto shrink-0 whitespace-nowrap rounded-full bg-mahiteal-500 text-white text-sm font-semibold font-mahi-sans px-5 py-2.5 shadow-[0_10px_24px_-8px_rgba(29,162,132,0.55)] active:scale-95 transition-transform inline-flex items-center gap-1.5"
                    >
                      {tryOnStatus === 'failed'
                        ? (<><RotateCcw className="w-4 h-4" /> Try again</>)
                        : 'See it on me'}
                    </button>
                  )}
                </div>
                {buyLink(product) && (
                  <a
                    href={buyLink(product)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onPointerDownCapture={(e) => { e.stopPropagation(); suppressNextTap(); }}
                    onClick={(e) => {
                      e.stopPropagation();
                      recordBuyIntent(product.mira_sku, 'discovery');
                    }}
                    data-testid="product-buy-link"
                    className="pointer-events-auto shrink-0 whitespace-nowrap ml-auto rounded-full bg-white text-slate-900 text-sm font-semibold font-mahi-sans px-5 py-2.5 shadow-sm active:scale-95 transition-transform"
                  >
                    Buy Now
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
